import { createColumnHelper } from '@tanstack/react-table';
import { LocalizedText, LocalizedEnum } from '@mews/framework';
import { WithAccountId } from '@mews-commander/types';
import { ConvertedCurrencyValue, LocalizedDateTimeFromUtc } from '@mews-commander/ui';
import { TextKey } from '@mews-commander/enums';
import { Tag } from '@optimus-web/core';
import { PaymentRequestReason, PaymentRequestState, PaymentRequestTableRecord } from '@mews-commander/data-access';
import { getTagVariantFromPaymentRequestState } from '@mews-commander/utils';
import { PaymentRequestActionsMenu } from './PaymentRequestActionsMenu';

const columnHelper = createColumnHelper<PaymentRequestTableRecord>();

export const getPaymentRequestsColumns = ({
    accountId,
    isCompany = false,
}: { isCompany?: boolean } & WithAccountId) => [
    columnHelper.accessor('amount', {
        header: () => <LocalizedText textKey={TextKey.Amount} />,
        cell: ({
            row: {
                original: { amount },
            },
        }) => {
            return <ConvertedCurrencyValue amount={amount} />;
        },
    }),
    columnHelper.accessor('expirationUtc', {
        header: () => <LocalizedText textKey={TextKey.Expiration} />,
        cell: ({
            row: {
                original: { expirationUtc },
            },
        }) => <LocalizedDateTimeFromUtc utcString={expirationUtc} />,
    }),
    columnHelper.accessor('createdUtc', {
        header: () => <LocalizedText textKey={TextKey.Created} />,
        cell: ({
            row: {
                original: { createdUtc },
            },
        }) => <LocalizedDateTimeFromUtc utcString={createdUtc} />,
    }),
    columnHelper.accessor('state', {
        header: () => <LocalizedText textKey={TextKey.Status} />,
        cell: ({
            row: {
                original: { state },
            },
        }: {
            row: { original: { state: PaymentRequestState } };
        }) => (
            <Tag variant={getTagVariantFromPaymentRequestState(state)} outline>
                <LocalizedEnum prefix="PaymentRequestState" value={state} />
            </Tag>
        ),
    }),
    columnHelper.accessor('reason', {
        header: () => <LocalizedText textKey={TextKey.Reason} />,
        cell: ({
            row: {
                original: { reason },
            },
        }: {
            row: { original: { reason: PaymentRequestReason } };
        }) => <LocalizedEnum prefix="PaymentRequestReason" value={reason} />,
    }),
    columnHelper.accessor('notes', {
        header: () => <LocalizedText textKey={TextKey.InternalNote} />,
    }),
    columnHelper.accessor('description', {
        header: () => <LocalizedText textKey={TextKey.MessageForCustomer} />,
    }),
    columnHelper.accessor('id', {
        header: () => <LocalizedText textKey={TextKey.ActionPlural} />,
        meta: {
            align: 'end',
        },
        cell: ({ row: { original: paymentRequest } }) => (
            <PaymentRequestActionsMenu accountId={accountId} paymentRequest={paymentRequest} isCompany={isCompany} />
        ),
    }),
];
